// Generated from scripts/quantity-selector.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
function toInteger(value) {
    if (value === null || value === undefined || value === '')
        return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}
/**
 * A quantity stepper: a native number input between a decrease and an increase button. The limits are the
 * variant's quantity rule, which Liquid writes on the input as `min`, `max` and `step`; the quantity the cart
 * already holds is `data-cart-quantity`. On a product page the rule limits how much more can be added, so the
 * effective maximum is the maximum minus what is in the cart; the cart line subclass limits the total.
 *
 * Every change is broadcast as a `QuantitySelectorUpdateEvent` and the owner of the surface reacts to it, so the
 * product form, the cart items and the quick order list each decide what a change means. The selector never
 * explains itself: a value the shopper typed that had to be corrected is kept as a correction, which the product
 * form takes with `takeCorrection` and says in words next to the stepper.
 *
 * Contract kept from the inherited module: the `quantity-selector-component` element, the `quantityInput`,
 * `minusButton` and `plusButton` refs, the public methods and the `data-cart-quantity` attribute. The module also
 * defines `cart-quantity-selector-component`, the stepper of a cart line.
 */
export class QuantitySelectorComponent extends Component {
    requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
    serverDisabledMinus = false;
    serverDisabledPlus = false;
    initialized = false;
    #correction;
    get minusButton() {
        const button = this.refs.minusButton;
        if (!(button instanceof HTMLButtonElement))
            throw new Error('Missing <button ref="minusButton" /> inside <quantity-selector-component />');
        return button;
    }
    get plusButton() {
        const button = this.refs.plusButton;
        if (!(button instanceof HTMLButtonElement))
            throw new Error('Missing <button ref="plusButton" /> inside <quantity-selector-component />');
        return button;
    }
    /** The number input, which is also the field that submits `quantity` with the form. */
    get quantityInput() {
        const input = this.refs.quantityInput;
        if (!(input instanceof HTMLInputElement))
            throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
        return input;
    }
    connectedCallback() {
        super.connectedCallback();
        // A control the server rendered disabled stays disabled; the selector manages the others.
        this.serverDisabledMinus = this.minusButton.disabled;
        this.serverDisabledPlus = this.plusButton.disabled;
        this.initialized = true;
        this.updateButtonStates();
    }
    /** Updates the quantity the cart holds for this variant and refreshes the component state. */
    setCartQuantity(cartQty) {
        this.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
        this.updateCartQuantity();
    }
    /** Whether the quantity in the input can be added without exceeding the maximum. */
    canAddToCart() {
        const { max, cartQuantity, value } = this.getCurrentValues();
        return {
            canAdd: !(max !== null && cartQuantity + value > max),
            maxQuantity: max,
            cartQuantity,
            quantityToAdd: value,
        };
    }
    /**
     * Checks the quantity in the input against the whole rule, in the order a shopper fixes it: the minimum, then the
     * increment, then the maximum counting what the cart already holds.
     */
    check() {
        const { min, max, step, value, cartQuantity } = this.getCurrentValues();
        if (value < min)
            return { ok: false, reason: 'min', limit: min, value };
        if ((value - min) % step !== 0)
            return { ok: false, reason: 'increment', limit: step, value };
        if (max !== null && cartQuantity + value > max)
            return { ok: false, reason: 'max', limit: max, value };
        return { ok: true, value };
    }
    /** The correction made to the last value the shopper typed, once: the form says it and the selector forgets it. */
    takeCorrection() {
        const correction = this.#correction;
        this.#correction = undefined;
        return correction;
    }
    /**
     * Turns the whole stepper off or on, as the variant it belongs to can or cannot be bought. Off, the input and both
     * buttons are disabled and stay so; on, the buttons follow the value and the bounds again.
     */
    setDisabled(disabled) {
        this.quantityInput.disabled = disabled;
        this.serverDisabledMinus = disabled;
        this.serverDisabledPlus = disabled;
        if (disabled) {
            this.minusButton.disabled = true;
            this.plusButton.disabled = true;
        }
        else {
            this.updateButtonStates();
        }
    }
    /** The current value, as the input holds it. */
    getValue() {
        return this.quantityInput.value;
    }
    setValue(value) {
        this.quantityInput.value = value;
    }
    /** Updates the rule from the input of another render and snaps the value to the new rule. */
    updateConstraints(min, max, step) {
        const input = this.quantityInput;
        const currentValue = toInteger(input.value) ?? 0;
        input.min = min;
        if (max)
            input.max = max;
        else
            input.removeAttribute('max');
        input.step = step;
        const newMin = toInteger(min) ?? 1;
        const newStep = toInteger(step) ?? 1;
        const effectiveMax = this.getEffectiveMax();
        // Snap down to the closest valid increment, then into the bounds.
        let newValue = currentValue;
        if ((currentValue - newMin) % newStep !== 0) {
            newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
        }
        newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));
        if (newValue !== currentValue)
            input.value = newValue.toString();
        this.updateButtonStates();
    }
    /** The limits and the value, read from the DOM on every call. */
    getCurrentValues() {
        const input = this.quantityInput;
        return {
            min: toInteger(input.min) ?? 1,
            max: toInteger(input.max) ?? null,
            step: toInteger(input.step) ?? 1,
            value: toInteger(input.value) ?? 0,
            cartQuantity: toInteger(input.getAttribute('data-cart-quantity')) ?? 0,
        };
    }
    /**
     * The highest value the stepper offers. On a product page that is how many more can be added: the maximum minus
     * what the cart holds, never below the minimum. Null without a maximum.
     */
    getEffectiveMax() {
        const { max, cartQuantity, min } = this.getCurrentValues();
        if (max === null)
            return null;
        return Math.max(max - cartQuantity, min);
    }
    /** Disables each button at its bound, except one the server rendered disabled. */
    updateButtonStates() {
        const { min, value } = this.getCurrentValues();
        const effectiveMax = this.getEffectiveMax();
        if (!this.serverDisabledMinus)
            this.minusButton.disabled = value <= min;
        if (!this.serverDisabledPlus)
            this.plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
    /** Moves the value by whole increments, within the bounds. */
    updateQuantity(stepMultiplier) {
        const { min, step, value } = this.getCurrentValues();
        const effectiveMax = this.getEffectiveMax();
        const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));
        this.quantityInput.value = newValue.toString();
        this.onQuantityChange();
        this.updateButtonStates();
    }
    increaseQuantity(event) {
        if (!(event.target instanceof HTMLElement))
            return;
        event.preventDefault();
        this.updateQuantity(1);
    }
    decreaseQuantity(event) {
        if (!(event.target instanceof HTMLElement))
            return;
        event.preventDefault();
        this.updateQuantity(-1);
    }
    /** Selects the whole value when the input gets focus, and keeps a re-render from replacing it while it is edited. */
    selectInputValue(event) {
        const input = this.quantityInput;
        if (!(event.target instanceof HTMLInputElement) || document.activeElement !== input)
            return;
        // A non-empty value, so the morph keeps the marker across a section render: its before-update hook ignores
        // empty-valued attributes.
        input.setAttribute('data-skip-value-update', 'true');
        input.select();
    }
    /**
     * Ends an edit of the input: the value snaps into the rule and a correction is kept for the form to explain. A
     * value above the maximum or below the minimum becomes the bound; a value between increments becomes the
     * increment below it. An empty field returns to the minimum without a word.
     */
    setQuantity(event) {
        if (!(event.target instanceof HTMLInputElement))
            return;
        event.preventDefault();
        const input = this.quantityInput;
        // Blur always ends the edit, so the morph opt-out is cleared before any branch can return early. Left set, the
        // morph would copy it onto every incoming server node and freeze the displayed value against later renders.
        input.removeAttribute('data-skip-value-update');
        const { min, step } = this.getCurrentValues();
        const effectiveMax = this.getEffectiveMax();
        const typed = toInteger(event.target.value);
        let quantity = typed ?? min;
        let correction;
        if (typed !== undefined) {
            if (typed < min) {
                quantity = min;
                correction = { ok: false, reason: 'min', limit: min, value: typed };
            }
            else if (effectiveMax !== null && typed > effectiveMax) {
                quantity = effectiveMax;
                correction = { ok: false, reason: 'max', limit: this.getCurrentValues().max ?? effectiveMax, value: typed };
            }
        }
        if ((quantity - min) % step !== 0) {
            quantity = min + Math.floor((quantity - min) / step) * step;
            correction ??= { ok: false, reason: 'increment', limit: step, value: typed ?? quantity };
        }
        this.#correction = correction;
        input.value = quantity.toString();
        this.onQuantityChange();
        this.updateButtonStates();
    }
    /** Broadcasts the new quantity. The cart line number rides along on the cart's steppers. */
    onQuantityChange() {
        const input = this.quantityInput;
        const value = toInteger(input.value);
        if (value === undefined)
            return;
        this.dispatchEvent(new QuantitySelectorUpdateEvent(value, toInteger(input.dataset.cartLine) || undefined));
    }
    /** Called when the quantity in the cart changed elsewhere: keeps the value inside the new bounds. */
    updateCartQuantity() {
        const input = this.quantityInput;
        const { min, value } = this.getCurrentValues();
        const effectiveMax = this.getEffectiveMax();
        const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));
        if (clampedValue !== value)
            input.value = clampedValue.toString();
        this.updateButtonStates();
    }
}
if (!customElements.get('quantity-selector-component')) {
    customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
/**
 * The stepper of a cart line. The value it holds is what the cart will hold in total, so its maximum is the
 * absolute maximum and not what is left after the cart's own quantity. Both buttons follow the value at all times,
 * because the cart re-renders its lines and the server never decides a stepper's state there.
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
    getEffectiveMax() {
        return this.getCurrentValues().max;
    }
    updateButtonStates() {
        const { min, value } = this.getCurrentValues();
        const effectiveMax = this.getEffectiveMax();
        this.minusButton.disabled = value <= min;
        this.plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
}
if (!customElements.get('cart-quantity-selector-component')) {
    customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
